import React, { useCallback, useState } from "react";

import { Button } from "components/atoms/Button";
import { Heading } from "components/atoms/Heading";
import { Image } from "components/atoms/Image";
import { Text } from "components/atoms/Text";
import { InformationCard } from "components/molecules/InformationCard";
import { Banner } from "components/organisms/Banner";
import { General } from "components/organisms/General";
import { Col, Row } from "components/organisms/Grid";
import { Section } from "components/organisms/Section";
import { Seo } from "helpers/seo";
import { getLinkThroughLocale, getOgByLanguage } from "helpers/utils";
import useMatchHeight from "hooks/useMatchHeight";
import MainBannerSection from "pages/Common/MainBanner";
import { downloadStaticFile } from "services/File";
import { IPageEnvironmentalProtection } from "services/Page/types";

interface Props {
	pageState: IPageEnvironmentalProtection;
}

const IndexPage: React.FC<Props> = ({ pageState }) => {
	const [isLoadedMore, setLoadMore] = useState<boolean>(true);

	const {
		banners,
		seoData,
		blocksData: { session1, session3, session2, ourFocus },
	} = pageState;

	const handleToggleLoadMore = useCallback(
		() => setLoadMore((prev) => !prev),
		[]
	);

	useMatchHeight(".ourFocusAreas_title");
	useMatchHeight(".ourFocusAreas_link");

	return (
		<General
			modifiers="marginbottom"
			helmet={
				<Seo
					seoData={seoData}
					ogData={getOgByLanguage(pageState.pageData?.translations)}
				/>
			}
		>
			<MainBannerSection
				title={banners?.[0]?.data?.title}
				description={banners?.[0]?.data?.description}
				imgPCSrc={banners?.[0]?.data?.imageDesktop}
				imgTABSrc={banners?.[0]?.data?.imageTablet}
				imgSPSrc={banners?.[0]?.data?.imageMobile}
			/>
			<Section useDiv>
				<Banner
					imgPCSrc={downloadStaticFile(session1?.image || "")}
					modifiers="inline"
					title={session1?.title}
					useParallaxScrolling
					useHeadingParagraph
				/>
			</Section>
			<Section useDiv>
				<Row>
					<Col className="u-mb-20 u-mb-md-0" xs="12" md="6">
						<Image
							alt={session1?.title || ""}
							aspectRatio="environmentalprotection"
							src={downloadStaticFile(session1?.imageLeft || "")}
						/>
					</Col>
					<Col
						xs="12"
						md="6"
						className="d-flex align-content-between flex-wrap"
					>
						<Text innerHtml={session1?.description} />

						<div className="u-mt-10">
							<Button
								iconName="arsenic-right-circle-arrow"
								href={getLinkThroughLocale(session1?.btn?.url)}
								target={session1?.btn?.target}
							>
								{session1?.btn?.text}
							</Button>
						</div>
					</Col>
				</Row>
			</Section>
			<Section isBorderBottom>
				<Heading>
					<span style={{ color: "#830051", fontSize: 28 }}>
						{ourFocus?.item?.[0].title}
					</span>
				</Heading>
				<Row className="mt-4">
					{ourFocus?.item?.[0].items.map((item, index) => (
						<Col
							xs="12"
							md="4"
							key={`${index.toString()}-${item?.title}`}
							className="focus-areas-col"
						>
							<div
								className="ourFocusAreas"
								style={{
									display: "flex",
									flexDirection: "column",
									justifyContent: "space-between",
								}}
							>
								<div>
									<img
										src={downloadStaticFile(item?.image)}
										alt={item?.title}
										style={{ marginBottom: 10 }}
									/>
									<div className="ourFocusAreas_title">
										<span style={{ color: "#3c4242", fontSize: 23 }}>
											{item?.title}
										</span>
									</div>
									<div
										dangerouslySetInnerHTML={{
											__html: item?.description || "",
										}}
										style={{
											marginTop: 10,
											color: "#3C4242",
											fontSize: 13,
											fontWeight: 300,
										}}
									/>
								</div>
								<div className="ourFocusAreas_link">
									<Button
										target={item.link.target}
										href={item.link.url}
										iconName="arsenic-right-circle-arrow"
										buttonType="outline"
										style={{
											justifyContent: "flex-start",
										}}
									>
										<span
											style={{
												color: "#3c4242",
												fontSize: 14,
												fontWeight: 600,
											}}
										>
											{item?.link?.text || ""}
										</span>
									</Button>
								</div>
							</div>
						</Col>
					))}
				</Row>
			</Section>
			<Section useDiv>
				<Section useDiv>
					<Banner
						imgPCSrc={downloadStaticFile(session2?.image || "")}
						modifiers="inline"
						title={session2?.title}
						useParallaxScrolling
						useHeadingParagraph
					/>
				</Section>
				<Section useDiv>
					{session2?.subTitle && (
						<div className="u-mb-20">
							<Heading type="h3" fontWeight="medium" fontStyle="italic">
								<span style={{ color: "#3c4242" }}>{session2.subTitle}</span>
							</Heading>
						</div>
					)}
					<Text innerHtml={session2?.description} />
				</Section>
				<Section useDiv>
					<Row className="u-mt-negative-20">
						{session2?.imageItem?.map(({ image, description }, index) => (
							<Col
								// eslint-disable-next-line react/no-array-index-key
								key={index}
								className="u-mt-20"
								xs="12"
								md="4"
							>
								<InformationCard
									description={description}
									imgAlt={session2?.title}
									imgSrc={downloadStaticFile(image)}
									type="activity"
								/>
							</Col>
						))}
					</Row>
				</Section>
				<Section useDiv>
					<div className="d-flex justify-content-center">
						<Button
							href={getLinkThroughLocale(session2?.btnLink)}
							iconName="arsenic-right-circle-arrow"
						>
							{session2?.btnLabel}
						</Button>
					</div>
				</Section>
			</Section>
			<Section>
				<Section>
					<Heading fontWeight="medium">
						<span style={{ color: "#3c4242" }}>{session3?.title}</span>
					</Heading>
				</Section>
				<Section useDiv>
					<Row className="u-mt-negative-20">
						{session3?.item
							?.slice(0, isLoadedMore ? 2 : undefined)
							?.map(({ image, title, description }, index) => (
								<Col
									// eslint-disable-next-line react/no-array-index-key
									key={index}
									className="u-mt-20"
									xs="12"
									md="6"
								>
									<InformationCard
										description={description}
										imgAlt={title}
										imgSrc={downloadStaticFile(image)}
										title={title}
										type="activity"
									/>
								</Col>
							))}
					</Row>
				</Section>

				{session3?.item && session3.item.length > 2 && (
					<Section useDiv>
						<div className="d-flex justify-content-center">
							<Button
								buttonType="outline"
								iconName={isLoadedMore ? "arsenic-plus" : "arsenic-minus"}
								onClick={handleToggleLoadMore}
							>
								{isLoadedMore ? session3?.btnLabel : session3?.btnLoadLess}
							</Button>
						</div>
					</Section>
				)}
			</Section>
		</General>
	);
};

export default IndexPage;
